import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { useNotificationRefresh } from '../contexts/NotificationContext'
import { usuarioService } from '../services/usuarioService'
import Table, { TableHeader, TableBody, TableRow, TableCell, TableHead } from '../components/ui/Table'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Listbox from '../components/ui/Listbox'
import Disclosure from '../components/ui/Disclosure'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import { Users, Plus, Search, RefreshCw, AlertCircle, Edit, Trash2, Eye, X, Filter, Mail, Phone, User as UserIcon, Key, Shield } from 'lucide-react'
import Avatar from '../components/ui/Avatar'

export default function Usuarios() {
  const { user } = useAuth()
  const { isAdmin } = usePermissions()
  const { refresh: refreshNotifications } = useNotificationRefresh()
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    search: '',
    tipo_usuario: '',
    activo: ''
  })
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  
  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedUsuario, setSelectedUsuario] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  
  // Estados para formularios
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    cedula: '',
    telefono: '',
    tipo_usuario: 'DUEÑO',
    activo: true
  })
  
  const [editFormData, setEditFormData] = useState({
    nombre: '',
    apellido: '',
    telefono: '',
    activo: true
  })
  
  const [formErrors, setFormErrors] = useState({})
  const [editFormErrors, setEditFormErrors] = useState({})

  useEffect(() => {
    fetchUsuarios()
  }, [pagination.current_page, filtros])

  const fetchUsuarios = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        ...(filtros.search && { search: filtros.search }),
        ...(filtros.tipo_usuario && { tipo_usuario: filtros.tipo_usuario }),
        ...(filtros.activo !== '' && { activo: filtros.activo })
      }

      const response = await usuarioService.getAll(params)
      
      if (response.success) {
        if (response.data.data) {
          setUsuarios(response.data.data)
          setPagination({
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 15,
            total: response.data.total || 0,
            last_page: response.data.last_page || 1
          })
        } else {
          setUsuarios(Array.isArray(response.data) ? response.data : [])
        }
      } else {
        setError(response.error || 'Error al cargar usuarios')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching usuarios:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchUsuarioDetail = async (id) => {
    try {
      const response = await usuarioService.getById(id)
      if (response.success) {
        return response.data
      } else {
        setError(response.error || 'Error al cargar usuario')
        return null
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      return null
    }
  }

  const handleView = async (usuario) => {
    const detail = await fetchUsuarioDetail(usuario.id_usuario || usuario.idUsuario)
    if (detail) {
      setSelectedUsuario(detail)
      setShowDetailModal(true)
    }
  }

  const handleEdit = (usuario) => {
    setSelectedUsuario(usuario)
    setEditFormData({
      nombre: usuario.nombre || '',
      apellido: usuario.apellido || '',
      telefono: usuario.telefono || '',
      activo: usuario.activo !== undefined ? usuario.activo : true
    })
    setEditFormErrors({})
    setShowEditModal(true)
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setFormErrors({})
    setError(null)

    try {
      const response = await usuarioService.create(formData)
      if (response.success) {
        setShowCreateModal(false)
        resetForm()
        fetchUsuarios()
        // Refrescar notificaciones inmediatamente
        refreshNotifications()
      } else {
        if (response.details) {
          setFormErrors(response.details)
        } else {
          setError(response.error || 'Error al crear usuario')
        }
      }
    } catch (err) {
      setError(err.message || 'Error al crear usuario')
      if (err.response?.data?.details) {
        setFormErrors(err.response.data.details)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setEditFormErrors({})
    setError(null)

    try {
      const id = selectedUsuario.id_usuario || selectedUsuario.idUsuario
      const response = await usuarioService.update(id, editFormData)
      if (response.success) {
        setShowEditModal(false)
        setSelectedUsuario(null)
        fetchUsuarios()
      } else {
        if (response.details) {
          setEditFormErrors(response.details)
        } else {
          setError(response.error || 'Error al actualizar usuario')
        }
      }
    } catch (err) {
      setError(err.message || 'Error al actualizar usuario')
      if (err.response?.data?.details) {
        setEditFormErrors(err.response.data.details)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este usuario? (Se puede restaurar después)')) {
      return
    }

    try {
      const response = await usuarioService.delete(id)
      if (response.success) {
        fetchUsuarios()
      } else {
        setError(response.error || 'Error al eliminar usuario')
      }
    } catch (err) {
      setError(err.message || 'Error al eliminar usuario')
    }
  }

  const handleReactivate = async (id) => {
    if (!confirm('¿Reactivar este usuario y enviarle un enlace de acceso (restablecer contraseña)?')) {
      return
    }
    try {
      const response = await usuarioService.reactivar(id)
      if (response.success) {
        fetchUsuarios()
      } else {
        setError(response.error || 'Error al reactivar usuario')
      }
    } catch (err) {
      setError(err.message || 'Error al reactivar usuario')
    }
  }


  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      nombre: '',
      apellido: '',
      cedula: '',
      telefono: '',
      tipo_usuario: 'DUEÑO',
      activo: true
    })
    setFormErrors({})
    setError(null)
  }

  const canCreate = isAdmin
  const canEdit = isAdmin
  const canDelete = isAdmin
  const userTipo = user?.tipoUsuario || user?.tipo_usuario

  const getTipoUsuarioColor = (tipo) => {
    switch (tipo) {
      case 'SUPERADMIN': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'ADMIN': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
      case 'VETERINARIO': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      case 'DUEÑO': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
      case 'PET_SITTER': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  const clearFilters = () => {
    setFiltros({ search: '', tipo_usuario: '', activo: '' })
  }

  const hasActiveFilters = filtros.search || filtros.tipo_usuario || filtros.activo !== ''

  if (loading && usuarios.length === 0) {
    return <LoadingState message="Cargando usuarios..." size="lg" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Usuarios</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">Gestión de usuarios del sistema</p>
        </div>
        {canCreate && (
          <Button onClick={() => { setShowCreateModal(true); resetForm(); }} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Filtros */}
      <Disclosure 
        title="Filtros de Búsqueda"
        defaultOpen={hasActiveFilters}
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
      >
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              label="Búsqueda"
              leftIcon={Search}
              placeholder="Nombre, email..."
              value={filtros.search}
              onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
            />
            <Listbox
              label="Tipo de Usuario"
              options={[
                { value: '', label: 'Todos' },
                { value: 'SUPERADMIN', label: 'Super Admin' },
                { value: 'ADMIN', label: 'Admin' },
                { value: 'VETERINARIO', label: 'Veterinario' },
                { value: 'DUEÑO', label: 'Dueño' },
                { value: 'PET_SITTER', label: 'Pet Sitter' }
              ]}
              value={filtros.tipo_usuario}
              onChange={(value) => setFiltros({ ...filtros, tipo_usuario: value })}
            />
            <Listbox
              label="Estado de cuenta"
              options={[
                { value: '', label: 'Todos' },
                { value: '1', label: 'Cuenta activa' },
                { value: '0', label: 'Cuenta desactivada' }
              ]}
              value={filtros.activo}
              onChange={(value) => setFiltros({ ...filtros, activo: value })}
            />
            <div className="flex items-end">
              <Button onClick={fetchUsuarios} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpiar Filtros
              </Button>
            </div>
          )}
        </div>
      </Disclosure>

      {/* Tabla */}
      {usuarios.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No hay usuarios registrados"
          description="No se encontraron usuarios con los filtros seleccionados."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="hidden lg:table-cell">Teléfono</TableHead>
                  <TableHead>Estado de cuenta</TableHead>
                  <TableHead className="hidden xl:table-cell">Fecha Registro</TableHead>
                  {(canEdit || canDelete) && (
                    <TableHead>Acciones</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.map((usuario) => {
                  const isDeleted = usuario.deleted_at || usuario.deletedAt
                  const usuarioId = usuario.id_usuario || usuario.idUsuario
                  
                  return (
                  <TableRow 
                    key={usuarioId}
                    className={isDeleted ? 'opacity-60 bg-gray-50 dark:bg-gray-900/50' : ''}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center space-x-3">
                        <Avatar user={usuario} size="sm" />
                        <div className="flex items-center space-x-2">
                          <span>{usuario.nombre} {usuario.apellido || ''}</span>
                          {isDeleted && (
                            <Badge variant="danger" className="text-xs">
                              Eliminado
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{usuario.email}</TableCell>
                    <TableCell>
                      <Badge variant={
                        (usuario.tipo_usuario || usuario.tipoUsuario) === 'SUPERADMIN' ? 'danger' :
                        (usuario.tipo_usuario || usuario.tipoUsuario) === 'ADMIN' ? 'purple' :
                        (usuario.tipo_usuario || usuario.tipoUsuario) === 'VETERINARIO' ? 'info' :
                        (usuario.tipo_usuario || usuario.tipoUsuario) === 'DUEÑO' ? 'success' : 'warning'
                      }>
                        {usuario.tipo_usuario || usuario.tipoUsuario}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">{usuario.telefono || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={usuario.activo ? 'success' : 'danger'}>
                        {usuario.activo ? 'Cuenta activa' : 'Cuenta desactivada'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {usuario.fecha_registro || usuario.fechaRegistro 
                        ? new Date(usuario.fecha_registro || usuario.fechaRegistro).toLocaleDateString('es-ES')
                        : '-'}
                    </TableCell>
                    {(canEdit || canDelete) && (
                      <TableCell>
                        <div className="flex space-x-2">
                          {!isDeleted && (
                            <>
                              <button
                                onClick={() => handleView(usuario)}
                                className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors"
                                title="Ver detalles"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              {canEdit && (
                                <button
                                  onClick={() => handleEdit(usuario)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                                  title="Editar"
                                >
                                  <Edit className="w-5 h-5" />
                                </button>
                              )}
                              {canEdit && !usuario.activo && (
                                <button
                                  onClick={() => handleReactivate(usuarioId)}
                                  className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors"
                                  title="Reactivar y enviar enlace de acceso"
                                >
                                  <Key className="w-5 h-5" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDelete(usuarioId)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Paginación */}
      {pagination.last_page > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Mostrando <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> a{' '}
            <span className="font-medium">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> de{' '}
            <span className="font-medium">{pagination.total}</span> resultados
          </div>
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page - 1 })}
            >
              Anterior
            </Button>
            <div className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              Página {pagination.current_page} de {pagination.last_page}
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page + 1 })}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal Crear Usuario */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }} size="lg">
        <ModalHeader onClose={() => { setShowCreateModal(false); resetForm(); }}>
          Nuevo Usuario
        </ModalHeader>
        <form onSubmit={handleCreate}>
          <ModalContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  required
                  leftIcon={UserIcon}
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  error={formErrors.nombre?.[0]}
                />
                <Input
                  label="Apellido"
                  leftIcon={UserIcon}
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  error={formErrors.apellido?.[0]}
                />
              </div>
              <Input
                label="Email"
                type="email"
                required
                leftIcon={Mail}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={formErrors.email?.[0]}
              />
              <Input
                label="Contraseña"
                type="password"
                required
                leftIcon={Key}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={formErrors.password?.[0]}
                hint="Mínimo 6 caracteres"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Cédula"
                  value={formData.cedula}
                  onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                  error={formErrors.cedula?.[0]}
                />
                <Input
                  label="Teléfono"
                  type="tel"
                  leftIcon={Phone}
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  error={formErrors.telefono?.[0]}
                />
              </div>
              <Listbox
                label="Tipo de Usuario"
                required
                options={[
                  { value: 'DUEÑO', label: 'Dueño' },
                  { value: 'PET_SITTER', label: 'Pet Sitter' },
                  { value: 'VETERINARIO', label: 'Veterinario' },
                  ...(userTipo === 'SUPERADMIN' ? [{ value: 'ADMIN', label: 'Admin' }] : [])
                ]}
                value={formData.tipo_usuario}
                onChange={(value) => setFormData({ ...formData, tipo_usuario: value })}
                error={formErrors.tipo_usuario?.[0]}
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Crear Usuario
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Editar Usuario */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedUsuario(null); }} size="lg">
        <ModalHeader onClose={() => { setShowEditModal(false); setSelectedUsuario(null); }}>
          Editar Usuario
        </ModalHeader>
        <form onSubmit={handleUpdate}>
          <ModalContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  required
                  leftIcon={UserIcon}
                  value={editFormData.nombre}
                  onChange={(e) => setEditFormData({ ...editFormData, nombre: e.target.value })}
                  error={editFormErrors.nombre?.[0]}
                />
                <Input
                  label="Apellido"
                  leftIcon={UserIcon}
                  value={editFormData.apellido}
                  onChange={(e) => setEditFormData({ ...editFormData, apellido: e.target.value })}
                  error={editFormErrors.apellido?.[0]}
                />
              </div>
              <Input
                label="Teléfono"
                type="tel"
                leftIcon={Phone}
                value={editFormData.telefono}
                onChange={(e) => setEditFormData({ ...editFormData, telefono: e.target.value })}
                error={editFormErrors.telefono?.[0]}
              />
              {canDelete && (
                <Listbox
                  label="Estado de cuenta"
                  options={[
                    { value: true, label: 'Cuenta activa' },
                    { value: false, label: 'Cuenta desactivada' }
                  ]}
                  value={editFormData.activo}
                  onChange={(value) => setEditFormData({ ...editFormData, activo: value })}
                />
              )}
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowEditModal(false); setSelectedUsuario(null); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Guardar Cambios
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Ver Detalles Usuario */}
      <Modal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedUsuario(null); }} size="lg">
        <ModalHeader onClose={() => { setShowDetailModal(false); setSelectedUsuario(null); }}>
          Detalles del Usuario
        </ModalHeader>
        <ModalContent>
          {selectedUsuario && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Nombre</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedUsuario.nombre} {selectedUsuario.apellido || ''}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                  <p className="text-sm text-gray-900 dark:text-white">{selectedUsuario.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Tipo de Usuario</label>
                  <Badge variant={
                    (selectedUsuario.tipo_usuario || selectedUsuario.tipoUsuario) === 'SUPERADMIN' ? 'danger' :
                    (selectedUsuario.tipo_usuario || selectedUsuario.tipoUsuario) === 'ADMIN' ? 'purple' :
                    (selectedUsuario.tipo_usuario || selectedUsuario.tipoUsuario) === 'VETERINARIO' ? 'info' :
                    (selectedUsuario.tipo_usuario || selectedUsuario.tipoUsuario) === 'DUEÑO' ? 'success' : 'warning'
                  }>
                    {selectedUsuario.tipo_usuario || selectedUsuario.tipoUsuario}
                  </Badge>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Estado de cuenta</label>
                  <Badge variant={selectedUsuario.activo ? 'success' : 'danger'}>
                    {selectedUsuario.activo ? 'Cuenta activa' : 'Cuenta desactivada'}
                  </Badge>
                </div>
                {selectedUsuario.cedula && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Cédula</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedUsuario.cedula}</p>
                  </div>
                )}
                {selectedUsuario.telefono && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Teléfono</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedUsuario.telefono}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Fecha de Registro</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedUsuario.fecha_registro || selectedUsuario.fechaRegistro
                      ? new Date(selectedUsuario.fecha_registro || selectedUsuario.fechaRegistro).toLocaleString('es-ES')
                      : '-'}
                  </p>
                </div>
              </div>
              
              {(selectedUsuario.veterinarios && selectedUsuario.veterinarios.length > 0) && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Perfil Veterinario</label>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    {selectedUsuario.veterinarios.map((vet, index) => (
                      <div key={index} className="text-sm text-gray-900 dark:text-white">
                        <p><span className="font-medium">Especialidad:</span> {vet.especialidad || '-'}</p>
                        {vet.numero_colegiatura && <p><span className="font-medium">Colegiatura:</span> {vet.numero_colegiatura}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => { setShowDetailModal(false); setSelectedUsuario(null); }}>
            Cerrar
          </Button>
          {canEdit && selectedUsuario && (
            <Button onClick={() => {
              setShowDetailModal(false)
              handleEdit(selectedUsuario)
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  )
}
